import React, { memo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, Link, Loader, SearchBar, Text } from '@Sage/hoi-poi-ui';

import { debounce } from 'lib/helpers';
import { searchValues } from 'lib/services/valueLists';
import { useWeb5Context } from 'lib/Web5Context';
import SectionList from 'components/SectionList';
import { useSmartFieldsGrid } from '../../hooks';

import styles from './styles';
const useStyles = createUseStyles(styles, 'SmartFieldsValuesSection');

const SmartFieldsValuesSection = ({
    valueListItems,
    onConfigureValue,
    hasMore,
    onLoadMore,
    isLoading,
    valueListRelated,
    className = 'smartfields-values',
}) => {
    const { t, language } = useWeb5Context();
    const classes = useStyles();
    const searchDebouncedRef = useRef(null);
    const currentValueListRelated = useRef(valueListRelated);

    const { columns, rows, actions } = useSmartFieldsGrid({
        valueListItems,
        onConfigureValue,
        t,
    });

    if (!searchDebouncedRef.current || currentValueListRelated.current !== valueListRelated) {
        currentValueListRelated.current = valueListRelated;
        searchDebouncedRef.current = debounce(
            (text = '', resolve) => {
                searchValues({ entity: valueListRelated, text, lang: language })
                    .then(resolve)
                    .catch(() => resolve([]));
            },
            250,
            true,
        );
    }

    const onSearchValues = useCallback((str) => {
        return new Promise((resolve) => {
            searchDebouncedRef.current(str, ({ data }) => {
                if (data?.length > 0) {
                    resolve(
                        data.map((item) => ({
                            label: item.description,
                            value: item.id,
                        })),
                    );
                } else {
                    resolve([]);
                }
            });
        });
    }, []);

    const onSelectValue = useCallback(
        (item) => {
            if (item?.type === 'change' || !item) return;
            const found = valueListItems.find((value) => value.value === item.value);
            if (found) {
                onConfigureValue(found.value);
            }
        },
        [valueListItems, onConfigureValue],
    );

    const showValues = valueListItems && valueListItems.length > 0;

    if (!showValues) {
        return <Text>{t('cfm_label_no_values_available')}</Text>;
    }

    return (
        <div className={`${classes.root} ${className}`}>
            {valueListRelated && (
                <div className={classes.toolbar}>
                    <SearchBar
                        className={classes.toolbarSearch}
                        loadOptions={onSearchValues}
                        placeholder={t('cfm_placeholder_search')}
                        noOptionsPlaceholder={t('label_no_options')}
                        loadingPlaceholder={t('wait_loading')}
                        onChange={onSelectValue}
                    />
                </div>
            )}
            <SectionList
                id="smartfields-values-list"
                columns={columns}
                rows={rows}
                actions={actions}
                headerHeight={40}
                rowHeight={40}
                getRowNodeId={(data) => data.id}
                withoutOffset={true}
            />
            {hasMore && (
                <div className={classes.showMoreContainer}>
                    {isLoading && <Loader size="small" />}
                    {!isLoading && <Link onClick={onLoadMore}>{t('cfm_label_more_results')}</Link>}
                </div>
            )}
        </div>
    );
};

SmartFieldsValuesSection.propTypes = {
    valueListItems: PropTypes.array.isRequired,
    onConfigureValue: PropTypes.func.isRequired,
    hasMore: PropTypes.bool,
    onLoadMore: PropTypes.func,
    isLoading: PropTypes.bool,
    valueListRelated: PropTypes.string,
    className: PropTypes.string,
};

const arePropsEqual = (prevProps, nextProps) => {
    return (
        prevProps.valueListItems === nextProps.valueListItems &&
        prevProps.onConfigureValue === nextProps.onConfigureValue &&
        prevProps.hasMore === nextProps.hasMore &&
        prevProps.onLoadMore === nextProps.onLoadMore &&
        prevProps.isLoading === nextProps.isLoading &&
        prevProps.valueListRelated === nextProps.valueListRelated &&
        prevProps.className === nextProps.className
    );
};

export default memo(SmartFieldsValuesSection, arePropsEqual);
